import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Trash2, Send } from 'lucide-react';
import './os.css';
import {
  StatusBadge, RecordCard, ServiceHealthRail, AttentionCard, DecisionCard, MetricCard,
  ContextTabs, ActionBar, DetailDrawer, MobileFilterDrawer, ViewToggle, LoadingState,
  EmptyState, UnknownState, ErrorState, ConfirmModal, ActionSheet, CompactRow, DataTable,
} from './osPrimitives';

// Realistic mock shapes mirroring current ACE record fields (no backend calls).
const MOCK = {
  client: { id: 'CL-4821', name: 'Marcus Whitfield', company: 'Whitfield Capital', stage: 'VIP · Active', mode: 'Chauffeured', spend: '$48,200', status: 'ready' },
  service: { id: 'BK-9F2A1', type: 'Group Driven', client: 'Whitfield Capital', when: 'Sat · 6:00 PM', market: 'Atlanta', status: 'attention',
    health: [
      { key: 'booking', label: 'Booking', status: 'ready' },
      { key: 'payment', label: 'Payment', status: 'attention' },
      { key: 'vehicle', label: 'Vehicle', status: 'ready' },
      { key: 'driver', label: 'Driver', status: 'waiting' },
      { key: 'client', label: 'Client', status: 'ready' },
      { key: 'fulfillment', label: 'Fulfillment', status: 'unknown' },
    ] },
  partner: { id: 'AFF-DEMO7PT', name: 'Skyline Executive Livery', tier: 'PREFERRED', verify: 'Approved', status: 'ready' },
  task: { id: 'TSK-1180', title: 'Confirm award overdue · Skyline', reason: 'Award FAW-DEMOAW not confirmed past deadline', due: 'Due today', status: 'risk' },
  decision: { q: 'Own vs. access for the 3rd vehicle?', note: 'Based on 14 trips/quarter and current Garage.',
    options: [{ label: 'Ownership', note: 'Best if usage stays high', recommended: true }, { label: 'Membership Access', note: 'Lower commitment' }] },
};

const SECTIONS = [
  { id: 'foundation', label: 'Foundation' },
  { id: 'records', label: 'Records' },
  { id: 'lifecycle', label: 'Service' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'views', label: 'Views' },
  { id: 'states', label: 'States' },
  { id: 'overlays', label: 'Overlays' },
];

export default function OSKit() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('foundation');
  const [view, setView] = useState('cards');
  const [drawer, setDrawer] = useState(false);
  const [dTab, setDTab] = useState('overview');
  const [filter, setFilter] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [sheet, setSheet] = useState(false);

  return (
    <div className="os-root min-h-screen" data-testid="os-kit">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/os')} className="flex items-center gap-1 text-xs mb-2" style={{ color: 'var(--os-muted)' }} data-testid="kit-back">
          <ChevronLeft className="h-3 w-3" /> Back to OS
        </button>
        <p className="text-[11px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>UI-R2</p>
        <h1 className="os-display text-4xl sm:text-5xl">PRIMITIVE KIT</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--os-muted)' }}>Shared ACE OS 2.0 components against realistic Client / Service / Partner / Task / Decision shapes.</p>

        <div className="my-5"><ContextTabs tabs={SECTIONS} active={tab} onChange={setTab} testid="kit-tabs" /></div>

        {tab === 'foundation' && (
          <Section title="Status Badges + Metric Cards">
            <div className="flex flex-wrap gap-2 mb-5">
              <StatusBadge status="ready" /><StatusBadge status="attention" /><StatusBadge status="risk" />
              <StatusBadge status="unknown" /><StatusBadge status="waiting" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard label="Confirmation rate" value="92%" sample={48} window="180d" trend={4} />
              <MetricCard label="Completion rate" value="88%" sample={41} window="180d" trend={-2} />
              <MetricCard label="Open payouts" value="$3,450" sample={6} />
              <MetricCard label="Garage size" unknown window="unknown" />
            </div>
          </Section>
        )}

        {tab === 'records' && (
          <Section title="Record Cards (Client / Partner)">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <RecordCard kind="Client" title={MOCK.client.name} subtitle={`${MOCK.client.company} · ${MOCK.client.id}`} status={MOCK.client.status}
                meta={[{ label: 'Stage', value: MOCK.client.stage }, { label: 'Lifetime', value: MOCK.client.spend }]} onOpen={() => setDrawer(true)} testid="rc-client" />
              <RecordCard kind="Partner" title={MOCK.partner.name} subtitle={MOCK.partner.id} status={MOCK.partner.status}
                meta={[{ label: 'Tier', value: MOCK.partner.tier }, { label: 'Verify', value: MOCK.partner.verify }]} onOpen={() => setDrawer(true)} testid="rc-partner" />
              <AttentionCard title={MOCK.task.title} reason={MOCK.task.reason} status={MOCK.task.status} dueLabel={MOCK.task.due} onAction={() => setConfirm(true)} testid="rc-task" />
            </div>
          </Section>
        )}

        {tab === 'lifecycle' && (
          <Section title="Service Record + Health Rail">
            <div className="os-card p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div><p className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{MOCK.service.type}</p>
                  <h3 className="font-bold text-lg mt-1">{MOCK.service.client} · {MOCK.service.id}</h3>
                  <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{MOCK.service.when} · {MOCK.service.market}</p></div>
                <StatusBadge status={MOCK.service.status} />
              </div>
              <ServiceHealthRail items={MOCK.service.health} />
            </div>
          </Section>
        )}

        {tab === 'lifestyle' && (
          <Section title="Decision Card">
            <DecisionCard question={MOCK.decision.q} note={MOCK.decision.note} options={MOCK.decision.options} onChoose={() => setConfirm(true)} />
          </Section>
        )}

        {tab === 'views' && (
          <Section title="Card / Compact / Table toggle" right={<ViewToggle view={view} onChange={setView} />}>
            {view === 'cards' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[MOCK.client, MOCK.partner].map((r, i) => <RecordCard key={i} kind={i ? 'Partner' : 'Client'} title={r.name} subtitle={r.id} status={r.status} onOpen={() => setDrawer(true)} />)}
              </div>
            )}
            {view === 'compact' && (
              <div>
                <CompactRow title={MOCK.client.name} subtitle={MOCK.client.company} status={MOCK.client.status} right={MOCK.client.spend} onOpen={() => setDrawer(true)} />
                <CompactRow title={MOCK.partner.name} subtitle={MOCK.partner.tier} status={MOCK.partner.status} right="6 jobs" onOpen={() => setDrawer(true)} />
              </div>
            )}
            {view === 'table' && (
              <DataTable
                columns={[{ key: 'id', label: 'ID' }, { key: 'name', label: 'Name' }, { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> }]}
                rows={[MOCK.client, MOCK.partner]} onRow={() => setDrawer(true)} />
            )}
          </Section>
        )}

        {tab === 'states' && (
          <Section title="Loading / Empty / Unknown / Error">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LoadingState rows={2} />
              <EmptyState body="No records match the current filter." action={{ label: 'Clear filters', onClick: () => {} }} />
              <UnknownState />
              <ErrorState body="Could not reach the service." onRetry={() => {}} />
            </div>
          </Section>
        )}

        {tab === 'overlays' && (
          <Section title="Drawer · Filter · Confirm · Action Sheet · Action Bar">
            <div className="flex flex-wrap gap-2">
              <button className="os-btn-ghost" onClick={() => setDrawer(true)} data-testid="open-drawer">Open Detail Drawer</button>
              <button className="os-btn-ghost" onClick={() => setFilter(true)} data-testid="open-filter">Open Filter Drawer</button>
              <button className="os-btn-ghost" onClick={() => setConfirm(true)} data-testid="open-confirm">Open Confirm Modal</button>
              <button className="os-btn-ghost" onClick={() => setSheet(true)} data-testid="open-sheet">Open Action Sheet</button>
            </div>
            <div className="mt-5 os-card overflow-hidden">
              <div className="p-4"><p className="text-sm" style={{ color: 'var(--os-muted)' }}>A form-style panel with a sticky Action Bar:</p></div>
              <ActionBar primary={{ label: 'Save', onClick: () => {} }} secondary={[{ label: 'Cancel', onClick: () => {} }]} />
            </div>
          </Section>
        )}
      </div>

      <DetailDrawer open={drawer} title={MOCK.client.name} subtitle={`${MOCK.client.company} · ${MOCK.client.id}`}
        tabs={[{ id: 'overview', label: 'Overview' }, { id: 'driven', label: 'Driven' }, { id: 'money', label: 'Money' }]}
        activeTab={dTab} onTab={setDTab} onClose={() => setDrawer(false)}
        footer={<ActionBar primary={{ label: 'New Booking', onClick: () => {} }} secondary={[{ label: 'Message', onClick: () => {} }]} />}>
        {dTab === 'overview' && <div className="space-y-3">
          <div className="flex gap-2"><StatusBadge status="ready" label="Active" /><StatusBadge status="ready" label="VIP" /></div>
          <ServiceHealthRail items={MOCK.service.health} />
          <p className="text-sm" style={{ color: 'var(--os-muted)' }}>Assembles canonical CL-* + Auto Lifestyle profile + services + money. Read-only demo.</p>
        </div>}
        {dTab === 'driven' && <EmptyState title="No recent Driven trips" body="Trips will appear here." />}
        {dTab === 'money' && <div className="grid grid-cols-2 gap-3"><MetricCard label="Lifetime" value={MOCK.client.spend} /><MetricCard label="Open balance" value="$0.00" /></div>}
      </DetailDrawer>

      <MobileFilterDrawer open={filter} onClose={() => setFilter(false)} onApply={() => setFilter(false)}>
        <p className="text-sm" style={{ color: 'var(--os-muted)' }}>Status</p>
        <div className="flex flex-wrap gap-2"><StatusBadge status="ready" /><StatusBadge status="attention" /><StatusBadge status="risk" /></div>
      </MobileFilterDrawer>

      <ConfirmModal open={confirm} title="Confirm action" body="This is a material decision. Proceed?" confirmLabel="Proceed" danger
        onConfirm={() => setConfirm(false)} onClose={() => setConfirm(false)} />

      <ActionSheet open={sheet} title="Record actions" onClose={() => setSheet(false)}
        actions={[{ label: 'Send message', icon: Send }, { label: 'Delete', icon: Trash2, danger: true }]} />
    </div>
  );
}

const Section = ({ title, right, children }) => (
  <div className="os-animate" data-testid={`kit-section-${title.split(' ')[0].toLowerCase()}`}>
    <div className="flex items-center justify-between mb-3">
      <h2 className="os-display text-2xl">{title}</h2>{right}
    </div>
    {children}
  </div>
);
